import BN from 'bn.js'
import { sha256 } from '@noble/hashes/webcrypto'

export interface AlgorithmV6AData {
  salt1: string // base64
  salt2: string // base64
  g: number
  p: string // base64
}

export interface AccountPassword {
  hasPassword: boolean
  algorithmData: AlgorithmV6AData
  srpB: string // base64
  randomData: string // base64
  SrpID: number
  SrpType: string
}

export interface PasswordInput {
  srpID: number
  srpType: string
  a: string
  m1: string
}

/**
 * If atob is missing (older browsers), polyfill it.
 */
export function ensureAtobPolyfill() {
  if (typeof (globalThis as any).atob === 'function')
    return // Minimal atob polyfill for standard base64 (not URL-safe)
  ;(globalThis as any).atob = (b64: string): string => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
    const clean = b64.replace(/[^A-Za-z0-9+/=]/g, '')
    let output = ''
    let buffer = 0
    let bits = 0
    for (let i = 0; i < clean.length; i++) {
      const c = clean.charAt(i)
      if (c === '=') break
      const val = chars.indexOf(c)
      if (val < 0) continue
      buffer = (buffer << 6) | val
      bits += 6
      if (bits >= 8) {
        bits -= 8
        output += String.fromCharCode((buffer >> bits) & 0xff)
      }
    }
    return output
  }
}

function ensureBtoaPolyfill() {
  if (typeof (globalThis as any).btoa === 'function')
    return // Minimal btoa polyfill for standard base64 (not URL-safe)
  ;(globalThis as any).btoa = (bin: string): string => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
    let out = ''
    let i = 0
    while (i < bin.length) {
      const c1 = bin.charCodeAt(i++) & 0xff
      const c2 = i < bin.length ? bin.charCodeAt(i++) & 0xff : NaN
      const c3 = i < bin.length ? bin.charCodeAt(i++) & 0xff : NaN

      const b1 = c1 >> 2
      const b2 = ((c1 & 0x3) << 4) | (isNaN(c2) ? 0 : c2 >> 4)
      const b3 = isNaN(c2) ? 64 : ((c2 & 0xf) << 2) | (isNaN(c3) ? 0 : c3 >> 6)
      const b4 = isNaN(c3) ? 64 : c3 & 0x3f

      out +=
        chars.charAt(b1) +
        chars.charAt(b2) +
        chars.charAt(b3) +
        chars.charAt(b4)
    }
    return out
  }
}

export function normalizeBase64(b64: string): string {
  // normalize URL-safe to standard base64; padding is optional here because atob tolerates missing padding
  return b64.replace(/-/g, '+').replace(/_/g, '/').replace(/\s+/g, '')
}

export function base64ToBytes(b64: string): Uint8Array {
  ensureAtobPolyfill()
  const n = normalizeBase64(b64)
  const bin = (globalThis as any).atob(n)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff
  return out
}

export function bytesToBase64(bytes: Uint8Array): string {
  ensureBtoaPolyfill()
  let bin = ''
  for (let i = 0; i < bytes.length; i++)
    bin += String.fromCharCode(bytes[i] as number)
  return (globalThis as any).btoa(bin)
}

export function bytesToBN(bytes: Uint8Array): BN {
  // big-endian
  return new BN(bytes as any, 16, 'be') // typed array input
}

export function bnToBytes(n: BN): Uint8Array {
  // minimal big-endian (no leading zero)
  return new Uint8Array(n.toArray('be'))
}

// SRP byte width for padding (e.g., 2048-bit group => 256 bytes)
export const SrpByteSize = 256

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let total = 0
  for (const p of parts) total += p.length
  const out = new Uint8Array(total)
  let off = 0
  for (const p of parts) {
    out.set(p, off)
    off += p.length
  }
  return out
}

// H(data ...[]byte) []byte
export async function H(...data: Uint8Array[]): Promise<Uint8Array> {
  const merged = concatBytes(...data)
  return sha256(merged)
}

// SH(data, salt []byte) []byte
export async function SH(
  data: Uint8Array,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const b = concatBytes(salt, data, salt)
  return H(b)
}

// PH1(password, salt1, salt2 []byte) []byte
export async function PH1(
  password: Uint8Array,
  salt1: Uint8Array,
  salt2: Uint8Array,
): Promise<Uint8Array> {
  return await SH(await SH(password, salt1), salt2)
}

// PH2(password, salt1, salt2 []byte) []byte
export async function PH2(
  password: Uint8Array,
  salt1: Uint8Array,
  salt2: Uint8Array,
): Promise<Uint8Array> {
  return SH(await SH(await PH1(password, salt1, salt2), salt1), salt2)
}

// K(p, g *big.Int) []byte
export async function K(p: BN, g: BN): Promise<Uint8Array> {
  return H(concatBytes(Pad(p), Pad(g)))
}

// U(ga, gb *big.Int) []byte
export async function U(ga: BN, gb: BN): Promise<Uint8Array> {
  return H(concatBytes(Pad(ga), Pad(gb)))
}

// M(p, g *big.Int, s1, s2 []byte, ga, gb, sb *big.Int) []byte
export async function M(
  p: BN,
  g: BN,
  s1: Uint8Array,
  s2: Uint8Array,
  ga: BN,
  gb: BN,
  sb: BN,
): Promise<Uint8Array> {
  return H(
    await H(Pad(p)),
    await H(Pad(g)),
    await H(s1),
    await H(s2),
    await H(Pad(ga)),
    await H(Pad(gb)),
    await H(Pad(sb)),
  )
}

// Pad x to SrpByteSize bytes if needed.
export function Pad(x: BN): Uint8Array {
  let b = bnToBytes(x) // minimal big-endian
  if (b.length < SrpByteSize) {
    const out = new Uint8Array(SrpByteSize)
    // leading zeros already 0 by default
    out.set(b, SrpByteSize - b.length)
    b = out
  }
  return b
}

// generateHash is used to fill this api
// interface SetPasswordInput {
//     srpID: number
//     srpType: string
//     passwordHash: string
// }
export async function generateHash(
  algo: AlgorithmV6AData,
  passB64: string,
): Promise<Uint8Array> {
  const g = new BN(algo.g) // small generator
  const p = bytesToBN(base64ToBytes(algo.p))

  const pass = base64ToBytes(passB64)
  const salt1 = base64ToBytes(algo.salt1)
  const salt2 = base64ToBytes(algo.salt2)

  const x1Bytes = await PH2(pass, salt1, salt2)
  const x1 = bytesToBN(x1Bytes)

  // Use Montgomery reduction for efficient modPow: v1 = g^x1 mod p
  const red = BN.mont(p)
  const v1 = g.toRed(red).redPow(x1).fromRed()

  return bnToBytes(v1)
}

export async function generatePasswordInput(
  ap: AccountPassword,
  passB64: string,
): Promise<PasswordInput> {
  const g = new BN(ap.algorithmData.g) // small generator
  const p = bytesToBN(base64ToBytes(ap.algorithmData.p))
  const gb = bytesToBN(base64ToBytes(ap.srpB))
  const a = bytesToBN(base64ToBytes(ap.randomData))

  const sal1 = base64ToBytes(ap.algorithmData.salt1)
  const sal2 = base64ToBytes(ap.algorithmData.salt2)
  const password = base64ToBytes(passB64)

  const k = bytesToBN(await K(p, g))
  const x1 = bytesToBN(await PH2(password, sal1, sal2))
  const red = BN.mont(p)
  const v1 = g.toRed(red).redPow(x1).fromRed()
  const kv = k.mul(v1).umod(p)
  const ga = g.toRed(red).redPow(a).fromRed()
  const u = bytesToBN(await U(ga, gb))
  const t = gb.sub(kv).umod(p)
  const exp = u.mul(x1).add(a)
  const sa = t.toRed(red).redPow(exp).fromRed()

  const m1 = await M(p, g, sal1, sal2, ga, gb, sa)

  return {
    srpID: ap.SrpID,
    srpType: ap.SrpType,
    a: bytesToBase64(Pad(ga)),
    m1: bytesToBase64(m1),
  }
}

export { bytesToHex } from '@noble/hashes/utils'
